import axiosInstance from "../../utils/axiosInstance";
import { BASE_URL } from "../../config";

/**
 * Payment Service
 * Centralized API handler for all payment-related operations
 * Professional, reusable, with error handling
 */

class PaymentService {
  constructor(token) {
    this.token = token;
    this.headers = {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    };
  }

  // ─── FETCH OPERATIONS ───────────────────────────────────────
  /**
   * Get all payments for a specific month and year
   * @param {number} month - Month (1-12)
   * @param {number} year - Year (YYYY)
   * @returns {Promise<Array>} All payments for the month
   */
  async getPaymentsByMonthYear(month, year) {
    try {
      const response = await axiosInstance.get(
        `${BASE_URL}/engineer/payments`,
        {
          params: { month, year },
          headers: this.headers,
        }
      );
      return {
        success: true,
        data: response.data,
        message: "Payments fetched successfully",
      };
    } catch (error) {
      return this.handleError(error, "Failed to fetch payments");
    }
  }

  /**
   * Get single payment details
   * @param {number} paymentId - Payment ID
   * @returns {Promise<Object>} Payment data
   */
  async getPaymentById(paymentId) {
    try {
      const response = await axiosInstance.get(
        `${BASE_URL}/engineer/payment/${paymentId}`,
        { headers: this.headers }
      );
      return {
        success: true,
        data: response.data,
        message: "Payment fetched successfully",
      };
    } catch (error) {
      return this.handleError(error, "Failed to fetch payment");
    }
  }

  // ─── EDIT OPERATIONS ────────────────────────────────────────
  /**
   * Edit payment deductions and allowances
   * @param {number} paymentId - Payment ID
   * @param {Object} editData - Contains: petrolAllowance, lateDayDeduction, advanceDeduction, editRemark
   * @param {string} editedBy - Username of person editing
   * @returns {Promise<Object>} Updated payment data
   */
  async editPayment(paymentId, editData, editedBy) {
    try {
      const payload = {
        petrolAllowance: editData.petrolAllowance || 0,
        lateDayDeduction: editData.lateDayDeduction || 0,
        advanceDeduction: editData.advanceDeduction || 0,
        editRemark: editData.editRemark || "",
      };

      const response = await axiosInstance.patch(
        `${BASE_URL}/engineer/payment/${paymentId}`,
        payload,
        {
          params: { editedBy },
          headers: this.headers,
        }
      );

      return {
        success: true,
        data: response.data,
        message: "Payment updated successfully",
      };
    } catch (error) {
      return this.handleError(error, "Failed to update payment");
    }
  }

  /**
   * Mark payment as paid
   * @param {number} paymentId - Payment ID
   * @param {string} paidBy - Username of person marking as paid
   * @returns {Promise<Object>} Updated payment data with paid status
   */
  async markPaymentAsPaid(paymentId, paidBy) {
    try {
      const response = await axiosInstance.patch(
        `${BASE_URL}/engineer/payment/${paymentId}/paid`,
        {},
        {
          params: { paidBy },
          headers: this.headers,
        }
      );

      return {
        success: true,
        data: response.data,
        message: "Payment marked as paid successfully",
      };
    } catch (error) {
      return this.handleError(error, "Failed to mark payment as paid");
    }
  }

  // ─── ERROR HANDLING ─────────────────────────────────────────
  handleError(error, defaultMessage) {
    const errorMessage = error?.response?.data?.message || defaultMessage;
    const statusCode = error?.response?.status;

    let userMessage = errorMessage;

    // Provide context-specific error messages
    if (statusCode === 401) {
      userMessage = "Session expired. Please log in again.";
    } else if (statusCode === 403) {
      userMessage = "You don't have permission to perform this action.";
    } else if (statusCode === 404) {
      userMessage = "Payment record not found.";
    } else if (statusCode === 409) {
      userMessage = "This payment has already been processed.";
    } else if (statusCode === 500) {
      userMessage = "Server error. Please try again later.";
    } else if (!error?.response) {
      userMessage = "Network error. Check your connection.";
    }

    return {
      success: false,
      error: error?.response?.data || null,
      message: userMessage,
      statusCode,
    };
  }
}

export default PaymentService;